// Nexus v3 API — with token expiry handling + auth interceptor
const BACKEND   = '/api/backend';
const QW        = '/api/qw';
const VANTA_URL = '/api/vanta';
const EXCHANGER = '/api/exchanger';
const S4  = '/api/s4';
const S5  = '/api/s5';
const S6  = '/api/s6';
const S9  = '/api/s9';
const S13 = '/api/s13';
const S14 = '/api/s14';
const S17 = '/api/s17';
const QW_KEY = 'vgo-qw-secret-2026-mindstake';

const getToken = () => localStorage.getItem('nexus_token');

// Handle 401 — token expired or invalid
const handle401 = () => {
  localStorage.removeItem('nexus_token');
  // Redirect to login if not already there
  if (!window.location.pathname.includes('/login') &&
      !window.location.pathname.includes('/register')) {
    window.location.href = '/login?expired=1';
  }
};

const req = async (base, path, method='GET', body=null, auth=true, extra={}) => {
  const headers = {'Content-Type':'application/json', ...extra};
  if (auth && getToken()) headers['Authorization'] = `Bearer ${getToken()}`;
  try {
    const res = await fetch(`${base}${path}`,{method,headers,body:body?JSON.stringify(body):null});

    // Handle token expiry
    if (res.status === 401 && auth) {
      handle401();
      throw new Error('Session expired. Please sign in again.');
    }

    if (!res.ok){
      const e=await res.json().catch(()=>({detail:'Failed'}));
      throw new Error(e.detail||e.error||`HTTP ${res.status}`);
    }
    return res.json();
  } catch(e){
    if(e.name==='TypeError') throw new Error('Service unavailable. Check connection.');
    throw e;
  }
};

const qwReq = (path,method='GET',body=null) =>
  req(QW,path,method,body,false,{'x-api-key':QW_KEY});

export const auth = {
  register:      d      => req(BACKEND,'/v1/auth/register','POST',d,false),
  login:         d      => req(BACKEND,'/v1/auth/login','POST',d,false),
  me:            ()     => req(BACKEND,'/v1/auth/me'),
  sendOtp:       email  => req(BACKEND,'/v1/otp/send-email','POST',{email},false),
  verifyOtp:     (e,c)  => req(BACKEND,'/v1/otp/verify-email','POST',{email:e,code:c},false),
  resetPassword: d      => req(BACKEND,'/v1/auth/reset-password','POST',d,false),
  logout:        ()     => { localStorage.removeItem('nexus_token'); },
};

export const wallet = {
  balance: async uid => {
    // Backend reads from QW, returns fresh balance every time
    const r = await req(BACKEND,'/v1/wallet','GET').catch(e => {
      console.error('[wallet.balance] error:', e);
      return {txc_balance:0,vgc_balance:0,vgs_balance:0};
    });
    return {
      txc_balance: r.txc_balance||0,
      vgc_balance: r.vgc_balance||0,
      vgs_balance: r.vgs_balance||0,
    };
  },
  createWallet: uid => qwReq('/vgo/wallet','POST',{user_id:uid}),
  send: async (from,to,amount,note) => {
    // Backend handles UUID resolution and QW payment
    return req(BACKEND,'/v1/wallet/send','POST',{
      to, amount:parseFloat(amount), note:note||'VGO Transfer'
    });
  },
  history: uid => req(BACKEND, '/v1/wallet/transactions', 'GET'),
  setupShortcode:(uid,code)=>req(BACKEND,'/v1/wallet/shortcode','POST',{user_id:uid,code}),
  resolveCode: code=>req(BACKEND,`/v1/wallet/shortcode/${code}`),
  inbound: async uid=>{
    const c=await req(BACKEND,`/v1/wallet/shortcode/${uid}@vgo`).catch(()=>null);
    return{short_code:c?.code||`${uid}@vgo`,address:uid};
  },
};

export const exchanger = {
  resources:      ()           => req(EXCHANGER,'/exchanger/resources','GET',null,false),
  countryRes:     cc           => req(EXCHANGER,`/exchanger/resources/${cc}`,'GET',null,false),
  list:           cc           => req(EXCHANGER,`/exchanger/list?country_code=${cc||''}`, 'GET',null,false),
  register:       d            => req(EXCHANGER,'/exchanger/register','POST',d),
  inboundInit:    d            => req(EXCHANGER,'/exchanger/inbound/initiate','POST',d,false),
  inboundSent:    (ref,uid,pr) => req(EXCHANGER,`/exchanger/inbound/user-sent?order_ref=${ref}&user_id=${uid}&payment_ref=${pr||''}`, 'POST',null,false),
  outboundInit:   d            => req(EXCHANGER,'/exchanger/outbound/initiate','POST',d,false),
  outboundConfirm:d            => req(EXCHANGER,'/exchanger/outbound/user-confirm','POST',d,false),
  myOrders:       uid          => req(EXCHANGER,`/exchanger/orders/user/${uid}`,'GET',null,false),
  getById:        id           => req(EXCHANGER,`/exchanger/${id}`,'GET',null,false),
  update:         (id,d)       => req(EXCHANGER,`/exchanger/${id}`,'PUT',d,false),
  dashboard:      id           => req(EXCHANGER,`/exchanger/${id}/dashboard`,'GET',null,false),
  exchangerOrders:(id,status,dir) => req(EXCHANGER,`/exchanger/${id}/orders${status?'?status='+status:''}${dir?((status?'&':'?')+'direction='+dir):''}`, 'GET',null,false),
  toggleOnline:   (id,online)  => req(EXCHANGER,`/exchanger/${id}/online?online=${online}`,'PUT',null,false),
  confirmInbound: d            => req(EXCHANGER,'/exchanger/inbound/confirm','POST',d,false),
  confirmOutbound:d            => req(EXCHANGER,'/exchanger/outbound/exchanger-sent','POST',d,false),
  findByUser:     uid          => req(EXCHANGER,`/exchanger/list?user_id=${uid}`,'GET',null,false),
  rate:           d            => req(EXCHANGER,'/exchanger/rate','POST',d,false),
};

export const exchange = {
  rates:     ()  => req(S13,'/exchange/rates','GET',null,false),
  symbols:   ()  => req(S13,'/dbux/symbols','GET',null,false),
  placeOrder:d   => req(S13,'/dbux/order','POST',d),
  holdings:  uid => req(S13,`/dbux/holdings/${uid}`),
  plans:     ()  => req(S13,'/subscriptions/plans','GET',null,false),
  subscribe: d   => req(S13,'/subscriptions/subscribe','POST',d),
  buy:       d   => req(S13,'/exchange/fiat-to-txc','POST',d),
};

export const quantumNode = {
  order:    d      => req(BACKEND,'/v1/hivecloud/order','POST',d),
  myNodes:  ()     => req(BACKEND,'/v1/hivecloud/my'),
  myOrders: ()     => req(BACKEND,'/v1/hivecloud/orders'),
  myEarnings: ()   => req(BACKEND,'/v1/hivecloud/earnings'),
  reqOtp:   d      => req(BACKEND,'/v1/hivecloud/activate/request','POST',d,false),
  activate: d      => req(BACKEND,'/v1/hivecloud/activate/verify','POST',d,false),
  earnings: serial => req(BACKEND,`/v1/hivecloud/earnings/${serial}`),
};

export const mindstake = {
  quizzes:    cat => req(S4,`/edu/quizzes${cat?`?category=${cat}`:''}`, 'GET',null,false),
  quiz:       id  => req(S4,`/edu/quiz/${id}`,'GET',null,false),
  categories: ()  => req(S4,'/edu/categories','GET',null,false),
  start:      d   => req(S4,'/edu/attempt/start','POST',d),
  submit:     d   => req(S4,'/edu/attempt/submit','POST',d),
  leaderboard:()  => req(S4,'/edu/leaderboard','GET',null,false),
  courses:    ()  => req(S4,'/edu/courses','GET',null,false),
  generate:   d   => req(S4,'/edu/quiz/generate','POST',d),
};

export const order = {
  products:    cat => req(S6,`/commerce/products${cat?`?category=${cat}`:''}`, 'GET',null,false),
  placeOrder:  d   => req(S6,'/commerce/order','POST',d),
  restaurants: ()  => req(S9,'/food/restaurants','GET',null,false),
  menu:        id  => req(S9,`/food/menu/${id}`,'GET',null,false),
  foodOrder:   d   => req(S9,'/food/order','POST',d),
  travel:      ()  => req(S17,'/travel/packages','GET',null,false),
  jobs:        ()  => req(S14,'/network/jobs','GET',null,false),
  doctors:     ()  => req(S5,'/health/doctors','GET',null,false),
};

export const vanta = {
  status: ()  => req(VANTA_URL,'/vanta/status','GET',null,false),
  chat:   msg => req(VANTA_URL,'/user/chat','POST',{message:msg}),
};

export const kyc = {
  submit: d  => req(BACKEND,'/v1/kyc/submit','POST',d),
  status: () => req(BACKEND,'/v1/kyc/status'),
};

export const modules = {
  manifest: () => req(BACKEND,'/v1/modules/manifest','GET',null,false),
};

export const push = {
  request: async () => {
    if(!('Notification' in window)) return false;
    return (await Notification.requestPermission())==='granted';
  },

  // Register service worker + subscribe to VAPID
  subscribe: async () => {
    try {
      if(!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
      const permission = await Notification.requestPermission();
      if(permission !== 'granted') return false;

      // Register service worker
      const reg = await navigator.serviceWorker.register('/push-sw.js');
      await navigator.serviceWorker.ready;

      // Get VAPID public key
      const res = await fetch('/api/backend/v1/push/vapid-key', {
        headers: { Authorization: 'Bearer '+localStorage.getItem('nexus_token') }
      });
      const { publicKey } = await res.json();

      // Convert VAPID key
      const vapidKey = urlBase64ToUint8Array(publicKey);

      // Subscribe
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey,
      });

      // Send subscription to backend
      await fetch('/api/backend/v1/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer '+localStorage.getItem('nexus_token'),
        },
        body: JSON.stringify({
          endpoint: sub.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('auth')))),
          },
          platform: 'web',
        }),
      });
      return true;
    } catch(e) {
      console.error('Push subscribe failed:', e);
      return false;
    }
  },

  show: (title, body, url='/home') => {
    if(Notification.permission==='granted')
      navigator.serviceWorker?.ready.then(r=>r.showNotification(title,{
        body, icon:'/logo192.png', badge:'/logo192.png',
        data:{url}, vibrate:[100,50,100],
      }));
  },

  test: () => req(BACKEND, '/v1/push/test', 'POST', {}),
};

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

export const roleDashboard = {
  get:           roleId => req(BACKEND, `/v1/role/${roleId}`,    'GET'),
  addTeamMember: d      => req(BACKEND, '/v1/role/team/add',     'POST', d),
  updateMember:  d      => req(BACKEND, '/v1/role/team/update',  'POST', d),
  postJob:       d      => req(BACKEND, '/v1/jobs/post',         'POST', d),
  listJobs:      ()     => req(BACKEND, '/v1/jobs',              'GET', null, false),
  listProjects:  ()     => req(BACKEND, '/v1/projects',          'GET', null, false),
};

export const roles = {
  my:         ()  => req(BACKEND, '/v1/roles/my'),
  activate:   d   => req(BACKEND, '/v1/roles/activate',    'POST', d),
  deactivate: d   => req(BACKEND, '/v1/roles/deactivate',  'POST', d),
};

export const orders = {
  // Type 1 — Direct
  create:     d   => req(BACKEND, '/v1/orders/direct',         'POST', d),
  my:         ()  => req(BACKEND, '/v1/orders/my'),
  // Type 2 — RFQ
  rfqCreate:  d   => req(BACKEND, '/v1/orders/rfq',            'POST', d),
  rfqList:    ()  => req(BACKEND, '/v1/orders/rfq/list'),
  rfqMy:      ()  => req(BACKEND, '/v1/orders/rfq/my'),
  rfqQuote:   d   => req(BACKEND, '/v1/orders/rfq/quote',      'POST', d),
  rfqQuotes:  id  => req(BACKEND, `/v1/orders/rfq/${id}/quotes`),
  rfqAccept:  d   => req(BACKEND, '/v1/orders/rfq/accept',     'POST', d),
  rfqAwarded:        () => req(BACKEND, '/v1/orders/rfq/awarded', 'GET'),
  directCreate:      d  => req(BACKEND, '/v1/orders/direct', 'POST', d),
  myDirectOrders:    () => req(BACKEND, '/v1/orders/my', 'GET'),
  directUpdateStatus: (id, status) => req(BACKEND, `/v1/orders/${id}/status`, 'POST', {status}),
    rfqMarkDelivered: id => req(BACKEND, `/v1/orders/rfq/${id}/delivered`, 'POST'),
  rfqConfirmReceipt: id => req(BACKEND, `/v1/orders/rfq/${id}/confirm`, 'POST'),
  myDeals: () => req(BACKEND, '/v1/deals/my', 'GET'),
};

export const serviceBookings = {
  create: (data) => post('/v1/bookings/service', data),
  my: () => get('/v1/bookings/my'),
};

export const homeTiles = {
  get:   ()  => req(BACKEND, '/v1/home/tiles',       'GET'),
  save:  (d) => req(BACKEND, '/v1/home/tiles',       'POST', d),
  track: (d) => req(BACKEND, '/v1/home/tiles/track', 'POST', d),
};

export const categories = {
  all:     () => req(BACKEND, '/v1/categories/all',     'GET', null, false),
  parents: () => req(BACKEND, '/v1/categories/parents', 'GET', null, false),
};

export const banners = {
  home: () => req(BACKEND, '/v1/banners/home', 'GET', null, false),
};

export const matrimony = {
  saveProfile:       d   => req(BACKEND, '/v1/matrimony/profile',             'POST', d),
  myProfile:         ()  => req(BACKEND, '/v1/matrimony/profile',             'GET'),
  browse:            (f) => req(BACKEND, `/v1/matrimony/browse${f?('?'+new URLSearchParams(f).toString()):''}`, 'GET'),
  sendInterest:      d   => req(BACKEND, '/v1/matrimony/interest',            'POST', d),
  interestsReceived: ()  => req(BACKEND, '/v1/matrimony/interests/received',  'GET'),
  interestsSent:     ()  => req(BACKEND, '/v1/matrimony/interests/sent',      'GET'),
  respondInterest:   (id,d) => req(BACKEND, `/v1/matrimony/interests/${id}/respond`, 'POST', d),
};

export const earn = {
  config: () => req(BACKEND, '/v1/earn/config', 'GET', null, false),
  activities:     ()  => req(BACKEND, '/v1/earn/activities',       'GET',  null, false),
  checkin:        ()  => req(BACKEND, '/v1/earn/checkin',          'POST', {}),
  streakStatus:   ()  => req(BACKEND, '/v1/earn/streak',           'GET'),
  scratch:        ()  => req(BACKEND, '/v1/earn/scratch',          'POST', {}),
  scratchStatus:  ()  => req(BACKEND, '/v1/earn/scratch/status',   'GET'),
  referralInfo:   ()  => req(BACKEND, '/v1/earn/referral',         'GET'),
  applyReferral:  d   => req(BACKEND, '/v1/earn/referral/apply',   'POST', d),
  leaderboard:    ()  => req(BACKEND, '/v1/earn/leaderboard',      'GET',  null, false),
  submitScore:    d   => req(BACKEND, '/v1/earn/score',            'POST', d),
};

export const startups = {
  list:   ()  => req(BACKEND, '/v1/startups',              'GET', null, false),
  get:    id  => req(BACKEND, `/v1/startups/${id}`,        'GET', null, false),
  ledger: id  => req(BACKEND, `/v1/startups/${id}/ledger`, 'GET', null, false),
};

export const subscriptions = {
  all:       ()  => req(BACKEND, '/v1/subscriptions/plans',          'GET', null, false),
  byStartup: id  => req(BACKEND, `/v1/subscriptions/plans/${id}`,    'GET', null, false),
  mine:      ()  => req(BACKEND, '/v1/subscriptions/my',             'GET'),
  subscribe: d   => req(BACKEND, '/v1/subscriptions/subscribe',      'POST', d),
  cancel:    d   => req(BACKEND, '/v1/subscriptions/cancel',         'POST', d),
};

export const market = {
  // Categories
  categories:    ()  => req(BACKEND, '/v1/market/categories',              'GET', null, false),

  // Consumer — requests
  postRequest:   d   => req(BACKEND, '/v1/market/request',                 'POST', d),
  myRequests:    ()  => req(BACKEND, '/v1/market/requests',                'GET'),
  getRequest:    id  => req(BACKEND, `/v1/market/request/${id}`,           'GET'),
  cancelRequest: d   => req(BACKEND, '/v1/market/request/cancel',          'POST', d),
  search:        p   => req(BACKEND, `/v1/market/search?${new URLSearchParams(p)}`, 'GET'),

  // Consumer — quotes
  acceptQuote:   d   => req(BACKEND, '/v1/market/accept',                  'POST', d),
  myOrders:      ()  => req(BACKEND, '/v1/market/orders',                  'GET'),
  confirmOrder:  d   => req(BACKEND, '/v1/market/confirm',                 'POST', d),
  dispute:       d   => req(BACKEND, '/v1/market/dispute',                 'POST', d),

  // Provider
  openRequests:  p   => req(BACKEND, `/v1/market/requests/open${p?'?'+new URLSearchParams(p):''}`, 'GET'),
  submitQuote:   d   => req(BACKEND, '/v1/market/quote',                   'POST', d),
  withdrawQuote: d   => req(BACKEND, '/v1/market/quote/withdraw',          'POST', d),
  providerOrders:()  => req(BACKEND, '/v1/provider/orders',                'GET'),
  reviews:       id  => req(BACKEND, `/v1/market/reviews/${id}`,           'GET', null, false),

  // Tracking (public)
  track:         tid => req(BACKEND, `/v1/market/track/${tid}`,            'GET', null, false),
};


export const delivery = {
  get:           id  => req(BACKEND, `/v1/delivery/${id}`,                 'GET'),
  update:        d   => req(BACKEND, '/v1/delivery/update',                'POST', d),
  verifyOtp:     d   => req(BACKEND, '/v1/delivery/otp',                   'POST', d),
  submitReceipt: d   => req(BACKEND, '/v1/delivery/receipt',               'POST', d),
  getReceipt:    id  => req(BACKEND, `/v1/delivery/receipt/${id}`,         'GET'),
  confirmReceipt:d   => req(BACKEND, '/v1/delivery/confirm',               'POST', d),
  registerAgent: d   => req(BACKEND, '/v1/delivery/agent/register',        'POST', d),
  updateLocation:d   => req(BACKEND, '/v1/delivery/agent/location',        'POST', d),
};

export const agreements = {
  check:  (type)    => req(BACKEND, `/v1/agreements/${type}`, 'GET'),
  accept: (type, version='1.0') => req(BACKEND, '/v1/agreements', 'POST', {agreement_type:type, version}),
};

export const bridge = {
  register:  d  => req(BACKEND, '/v1/bridge/register',  'POST', d),
  me:        ()  => req(BACKEND, '/v1/bridge/me',        'GET'),
  update:    d  => req(BACKEND, '/v1/bridge/update',    'POST', d),
  list:      p  => req(BACKEND, `/v1/bridge/list${p?'?'+new URLSearchParams(p):''}`, 'GET', null, false),
  confirm:   d  => req(BACKEND, '/v1/bridge/confirm',   'POST', d),
};

export const partner = {
  // Subscription
  plans:         ()       => req(BACKEND, '/v1/partner/plans',        'GET'),
  subscription:  ()       => req(BACKEND, '/v1/partner/subscription', 'GET'),
  activate:      (planId) => req(BACKEND, '/v1/partner/activate',     'POST', { plan_id: planId }),
  renew:         (planId) => req(BACKEND, '/v1/partner/renew',        'POST', { plan_id: planId }),
  cancel:        ()       => req(BACKEND, '/v1/partner/cancel',       'POST'),
  events:        ()       => req(BACKEND, '/v1/partner/events',       'GET'),
  // Legacy (kept for compatibility)
  register:      d        => req(BACKEND, '/v1/partner/register',     'POST', d),
  dashboard:     ()       => req(BACKEND, '/v1/partner/dashboard',    'GET'),
  commissions:   ()       => req(BACKEND, '/v1/partner/commissions',  'GET'),
  team:          ()       => req(BACKEND, '/v1/partner/team',         'GET'),
  structure:     ()       => req(BACKEND, '/v1/partner/structure',    'GET', null, false),
};

export const provider = {
  dashboard:      ()       => req(BACKEND, '/v1/provider/dashboard',         'GET'),
  notifications:  ()       => req(BACKEND, '/v1/provider/notifications',     'GET'),
  categories:     ()       => req(BACKEND, '/v1/provider/categories',        'GET', null, false),
  myCategories:   ()       => req(BACKEND, '/v1/provider/my-categories',     'GET'),
  expiring:       ()       => req(BACKEND, '/v1/provider/expiring',          'GET'),
  tier:           ()       => req(BACKEND, '/v1/provider/tier',              'GET'),
  analytics:      ()       => req(BACKEND, '/v1/provider/analytics',         'GET'),
  renew:          (data)   => req(BACKEND, '/v1/provider/renew',             'POST', data),
  subscribe:      (data)   => req(BACKEND, '/v1/provider/subscribe',         'POST', data),
  myServices:     ()       => req(BACKEND, '/v1/provider/services',          'GET'),
  addService:     (data)   => req(BACKEND, '/v1/provider/services/add',      'POST', data),
  updateService:  (id, d)  => req(BACKEND, `/v1/provider/services/${id}/update`, 'PUT', d),
  deleteService:  (id)     => req(BACKEND, `/v1/provider/services/${id}/delete`, 'DELETE'),
  browseRequests: (filters={}) => {
    const q = new URLSearchParams(Object.entries(filters).filter(([,v])=>v)).toString();
    return req(BACKEND, '/v1/provider/browse-requests' + (q?`?${q}`:''), 'GET');
  },
  acceptRequest:  (data)   => req(BACKEND, '/v1/provider/accept-request',    'POST', data),
  myOrders:       ()       => req(BACKEND, '/v1/provider/my-orders',         'GET'),
  updateOrder:    (id, d)  => req(BACKEND, `/v1/provider/my-orders/${id}`,   'PUT', d),
  orders:         ()       => req(BACKEND, '/v1/provider/my-orders',         'GET'),

  // Public consumer browse — no auth
  browseServices: (params) => req(BACKEND, `/v1/services?${new URLSearchParams(params || {}).toString()}`, 'GET'),
  categoriesPublic: () => req(BACKEND, '/v1/provider/categories', 'GET'),
};

export const trading = {
  market:       ()         => req(BACKEND, '/v1/trading/market',                    'GET', null, false),
  portfolio:    ()         => req(BACKEND, '/v1/trading/portfolio',                 'GET'),
  orders:       ()         => req(BACKEND, '/v1/trading/orders',                    'GET'),
  orderbook:    symbol     => req(BACKEND, `/v1/trading/orderbook/${symbol}`,       'GET', null, false),
  ohlcv:        (sym,tf)   => req(BACKEND, `/v1/trading/ohlcv/${sym}?timeframe=${tf||'1h'}&limit=100`, 'GET', null, false),
  placeOrder:   data       => req(BACKEND, '/v1/trading/order',                     'POST', data),
  cancelOrder:  id         => req(BACKEND, `/v1/trading/order/${id}/cancel`,         'POST'),
  alerts:       ()         => req(BACKEND, '/v1/trading/alerts',                    'GET'),
  createAlert:  data       => req(BACKEND, '/v1/trading/alert',                     'POST', data),
  deleteAlert:  id         => req(BACKEND, `/v1/trading/alert/${id}`,               'DELETE'),
};

export const rolesConfig = {
  fields:   (roleId) => req(BACKEND, `/v1/roles/fields/${roleId}`,  'GET', null, false),
  activate: (data)   => req(BACKEND, '/v1/roles/activate',          'POST', data),
  myRoles:  ()       => req(BACKEND, '/v1/roles/my',                'GET'),
  // below kept for backward compat:
  list: () => req(BACKEND, '/v1/roles/list', 'GET', null, false),
};


export const myOrders = {
  combined: () => req(BACKEND, '/v1/market/my-orders-combined', 'GET'),
};









export const campaigns = {
  list:    ()       => req(BACKEND, '/v1/campaigns',             'GET'),
  get:     id       => req(BACKEND, `/v1/campaigns/${id}`,       'GET'),
  create:  d        => req(BACKEND, '/v1/campaigns',             'POST', d),
  update:  (id, d)  => req(BACKEND, `/v1/campaigns/${id}`,       'PUT',  d),
  delete:  id       => req(BACKEND, `/v1/campaigns/${id}`,       'DELETE'),
  submit:  (id, d)  => req(BACKEND, `/v1/campaigns/${id}/submit`,'POST', d),
  // admin
  pending: ()       => req(BACKEND, '/v1/admin/campaigns',       'GET'),
  review:  (id, d)  => req(BACKEND, `/v1/admin/campaigns/${id}/review`,'POST', d),
};

export const ssiProducts = {
  list:    ()       => req(BACKEND, '/v1/ssi/products',          'GET'),
  get:     id       => req(BACKEND, `/v1/ssi/products/${id}`,    'GET'),
  create:  d        => req(BACKEND, '/v1/ssi/products',          'POST', d),
  update:  (id, d)  => req(BACKEND, `/v1/ssi/products/${id}`,    'PUT',  d),
  delete:  id       => req(BACKEND, `/v1/ssi/products/${id}`,    'DELETE'),
  submit:  (id)     => req(BACKEND, `/v1/ssi/products/${id}/submit`,'POST'),
  catalog: ()       => req(BACKEND, '/v1/ssi/catalog',           'GET', null, false),
  // admin
  pending: ()       => req(BACKEND, '/v1/admin/ssi/products',    'GET'),
  review:  (id, d)  => req(BACKEND, `/v1/admin/ssi/products/${id}/review`,'POST', d),
};



export const productExt = {
  // Images
  images:       id       => req(BACKEND, `/v1/ssi/products/${id}/images`,         'GET'),
  addImage:     (id, d)  => req(BACKEND, `/v1/ssi/products/${id}/images`,         'POST', d),
  delImage:     (id, iid)=> req(BACKEND, `/v1/ssi/products/${id}/images/${iid}`,  'DELETE'),
  setPrimary:   (id, iid)=> req(BACKEND, `/v1/ssi/products/${id}/images/${iid}/primary`, 'POST'),
  // Variants
  variants:     id       => req(BACKEND, `/v1/ssi/products/${id}/variants`,       'GET'),
  addVariant:   (id, d)  => req(BACKEND, `/v1/ssi/products/${id}/variants`,       'POST', d),
  updateVariant:(id, vid, d) => req(BACKEND, `/v1/ssi/products/${id}/variants/${vid}`, 'PUT', d),
  delVariant:   (id, vid)=> req(BACKEND, `/v1/ssi/products/${id}/variants/${vid}`,'DELETE'),
  // Pricing
  pricing:      id       => req(BACKEND, `/v1/ssi/products/${id}/pricing`,        'GET'),
  addPricing:   (id, d)  => req(BACKEND, `/v1/ssi/products/${id}/pricing`,        'POST', d),
  delPricing:   (id, tid)=> req(BACKEND, `/v1/ssi/products/${id}/pricing/${tid}`, 'DELETE'),
  // Stock
  stock:        id       => req(BACKEND, `/v1/ssi/products/${id}/stock`,          'GET'),
  restock:      (id, d)  => req(BACKEND, `/v1/ssi/products/${id}/stock`,          'POST', d),
};


export const addresses = {
  list:   ()       => req(BACKEND, '/v1/addresses',         'GET'),
  add:    d        => req(BACKEND, '/v1/addresses',         'POST', d),
  update: (id, d)  => req(BACKEND, `/v1/addresses/${id}`,   'PUT',  d),
  delete: id       => req(BACKEND, `/v1/addresses/${id}`,   'DELETE'),
};

export const ssiOrders = {
  get:       id           => req(BACKEND, `/v1/orders/${id}`,           'GET'),
  history:   id           => req(BACKEND, `/v1/orders/${id}/history`,   'GET'),
  myOrders:  ()           => req(BACKEND, '/v1/ssi/my-orders',          'GET'),
  mySales:   ()           => req(BACKEND, '/v1/ssi/my-sales',           'GET'),
  buy:       d            => req(BACKEND, '/v1/ssi/buy',                'POST', d),
  // Status transitions (producer)
  packing:   (id, d)      => req(BACKEND, `/v1/orders/${id}/packing`,   'POST', d || {}),
  shipped:   (id, d)      => req(BACKEND, `/v1/orders/${id}/shipped`,   'POST', d),
  delivered: (id, d)      => req(BACKEND, `/v1/orders/${id}/delivered`, 'POST', d || {}),
  cancel:    (id, d)      => req(BACKEND, `/v1/orders/${id}/cancel`,    'POST', d),
  // Refunds
  refund:       (id, d)   => req(BACKEND, `/v1/orders/${id}/refund`,    'POST', d),
  refundList:   ()        => req(BACKEND, '/v1/refunds',                'GET'),
  adminRefunds: ()        => req(BACKEND, '/v1/admin/refunds',          'GET'),
  processRefund:(id, d)   => req(BACKEND, `/v1/admin/refunds/${id}/process`, 'POST', d),
};


export const training = {
  // User-facing
  list:          (filter)  => req(BACKEND, `/v1/training/modules${filter?('?'+new URLSearchParams(filter).toString()):''}`, 'GET'),
  get:           id        => req(BACKEND, `/v1/training/modules/${id}`,                 'GET'),
  enroll:        id        => req(BACKEND, `/v1/training/modules/${id}/enroll`,          'POST'),
  completeLesson:(mid, lid, d) => req(BACKEND, `/v1/training/modules/${mid}/lessons/${lid}/complete`, 'POST', d || {}),
  getQuiz:       id        => req(BACKEND, `/v1/training/modules/${id}/quiz`,            'GET'),
  submitQuiz:    (id, d)   => req(BACKEND, `/v1/training/modules/${id}/quiz`,            'POST', d),
  myEnrollments: ()        => req(BACKEND, '/v1/training/my-enrollments',                'GET'),
  myCertificates:()        => req(BACKEND, '/v1/training/my-certificates',               'GET'),
  // Admin
  adminList:     ()        => req(BACKEND, '/v1/admin/training/modules',                 'GET'),
  adminCreate:   d         => req(BACKEND, '/v1/admin/training/modules',                 'POST', d),
  adminUpdate:   (id, d)   => req(BACKEND, `/v1/admin/training/modules/${id}`,           'PUT',  d),
  adminDelete:   id        => req(BACKEND, `/v1/admin/training/modules/${id}`,           'DELETE'),
  adminAddLesson:(mid, d)  => req(BACKEND, `/v1/admin/training/modules/${mid}/lessons`,  'POST', d),
  adminUpdateLesson:(mid, lid, d) => req(BACKEND, `/v1/admin/training/modules/${mid}/lessons/${lid}`, 'PUT', d),
  adminDeleteLesson:(mid, lid) => req(BACKEND, `/v1/admin/training/modules/${mid}/lessons/${lid}`, 'DELETE'),
  adminQuestions:(mid)     => req(BACKEND, `/v1/admin/training/modules/${mid}/questions`,'GET'),
  adminAddQuestion:(mid, d) => req(BACKEND, `/v1/admin/training/modules/${mid}/questions`,'POST', d),
  adminDeleteQuestion:(mid, qid) => req(BACKEND, `/v1/admin/training/modules/${mid}/questions/${qid}`, 'DELETE'),
};


export const investments = {
  // SSI / user
  list:        ()       => req(BACKEND, '/v1/investments',                      'GET'),
  get:         id       => req(BACKEND, `/v1/investments/${id}`,                'GET'),
  create:      d        => req(BACKEND, '/v1/investments',                      'POST', d),
  update:      (id, d)  => req(BACKEND, `/v1/investments/${id}`,                'PUT',  d),
  submitMilestone: (id, mid, d) => req(BACKEND, `/v1/investments/${id}/milestones/${mid}/submit`, 'POST', d),
  repay:       (id, d)  => req(BACKEND, `/v1/investments/${id}/repay`,          'POST', d),
  // Admin
  adminList:   ()       => req(BACKEND, '/v1/admin/investments',                'GET'),
  adminReview: (id, d)  => req(BACKEND, `/v1/admin/investments/${id}/review`,   'POST', d),
  adminAddMilestone: (id, d) => req(BACKEND, `/v1/admin/investments/${id}/milestones`, 'POST', d),
  adminVerifyMilestone: (id, mid, d) => req(BACKEND, `/v1/admin/investments/${id}/milestones/${mid}/verify`, 'POST', d),
  adminDisburse: (id, d) => req(BACKEND, `/v1/admin/investments/${id}/disburse`, 'POST', d),
};


export const analytics = {
  user:           (days)   => req(BACKEND, `/v1/analytics${days?'?days='+days:''}`,    'GET'),
  recommendations:()       => req(BACKEND, '/v1/analytics/recommendations',             'GET'),
  dismiss:        id       => req(BACKEND, `/v1/analytics/recommendations/${id}/dismiss`, 'POST'),
  action:         id       => req(BACKEND, `/v1/analytics/recommendations/${id}/action`, 'POST'),
  trackEvent:     d        => req(BACKEND, '/v1/analytics/event',                       'POST', d),
  adminPlatform:  (days)   => req(BACKEND, `/v1/admin/analytics${days?'?days='+days:''}`, 'GET'),
};


export const countryPartner = {
  apply:        d        => req(BACKEND, '/v1/country-partner/apply',          'POST', d),
  me:           ()       => req(BACKEND, '/v1/country-partner/me',             'GET'),
  territory:    ()       => req(BACKEND, '/v1/country-partner/territory',      'GET'),
  adminList:    ()       => req(BACKEND, '/v1/admin/country-partners',         'GET'),
  adminGet:     id       => req(BACKEND, `/v1/admin/country-partners/${id}`,   'GET'),
  adminReview:  (id, d)  => req(BACKEND, `/v1/admin/country-partners/${id}/review`, 'POST', d),
};


export const marketplace = {
  browse:    ()        => req(BACKEND, '/v1/marketplace/investments',                'GET'),
  get:       id        => req(BACKEND, `/v1/marketplace/investments/${id}`,          'GET'),
  list:      (id, d)   => req(BACKEND, `/v1/marketplace/investments/${id}/list`,     'POST', d),
  unlist:    id        => req(BACKEND, `/v1/marketplace/investments/${id}/unlist`,   'POST'),
  invest:    (id, d)   => req(BACKEND, `/v1/marketplace/investments/${id}/offer`,    'POST', d),
  myOffers:  ()        => req(BACKEND, '/v1/marketplace/my-offers',                  'GET'),
};


export const auctions = {
  browse:     (filter)  => req(BACKEND, `/v1/auctions${filter?('?'+new URLSearchParams(filter).toString()):''}`, 'GET'),
  get:        id        => req(BACKEND, `/v1/auctions/${id}`,                'GET'),
  create:     d         => req(BACKEND, '/v1/auctions',                      'POST', d),
  update:     (id, d)   => req(BACKEND, `/v1/auctions/${id}`,                'PUT', d),
  myAuctions: ()        => req(BACKEND, '/v1/auctions/my',                   'GET'),
  myBids:        ()        => req(BACKEND, '/v1/auctions/my-bids',              'GET'),
  bidsReceived:  ()        => req(BACKEND, '/v1/auctions/bids-received',        'GET'),
  publish:    id        => req(BACKEND, `/v1/auctions/${id}/publish`,        'POST'),
  cancel:     id        => req(BACKEND, `/v1/auctions/${id}/cancel`,         'POST'),
  payDeposit: id        => req(BACKEND, `/v1/auctions/${id}/deposit`,        'POST'),
  bid:        (id, d)   => req(BACKEND, `/v1/auctions/${id}/bid`,            'POST', d),
  payBalance: id        => req(BACKEND, `/v1/auctions/${id}/pay`,            'POST'),
  ship:       (id, d)   => req(BACKEND, `/v1/auctions/${id}/ship`,           'POST', d),
  confirmDelivery: id   => req(BACKEND, `/v1/auctions/${id}/delivered`,      'POST'),
};


export const work = {
  // Gigs
  browseGigs:    (filter)   => req(BACKEND, `/v1/work/gigs${filter?('?'+new URLSearchParams(filter).toString()):''}`, 'GET'),
  myGigs:        ()         => req(BACKEND, '/v1/work/gigs/my',                   'GET'),
  createGig:     d          => req(BACKEND, '/v1/work/gigs',                      'POST', d),
  getGig:        id         => req(BACKEND, `/v1/work/gigs/${id}`,                'GET'),
  updateGig:     (id, d)    => req(BACKEND, `/v1/work/gigs/${id}`,                'PUT', d),
  deleteGig:     id         => req(BACKEND, `/v1/work/gigs/${id}`,                'DELETE'),
  orderGig:      (id, d)    => req(BACKEND, `/v1/work/gigs/${id}/order`,          'POST', d),
  // Work Orders
  browseOrders:  (filter)   => req(BACKEND, `/v1/work/orders${filter?('?'+new URLSearchParams(filter).toString()):''}`, 'GET'),
  myOrders:      ()         => req(BACKEND, '/v1/work/orders/my',                 'GET'),
  createOrder:   d          => req(BACKEND, '/v1/work/orders',                    'POST', d),
  getOrder:      id         => req(BACKEND, `/v1/work/orders/${id}`,              'GET'),
  submitProposal:(id, d)    => req(BACKEND, `/v1/work/orders/${id}/proposals`,    'POST', d),
  acceptProposal:(oid, pid) => req(BACKEND, `/v1/work/orders/${oid}/proposals/${pid}/accept`, 'POST'),
  myProposals:        ()         => req(BACKEND, '/v1/work/proposals/my',              'GET'),
  proposalsReceived:  ()         => req(BACKEND, '/v1/work/proposals/received',        'GET'),
  // Jobs
  myJobs:        ()         => req(BACKEND, '/v1/work/jobs',                      'GET'),
  getJob:        id         => req(BACKEND, `/v1/work/jobs/${id}`,                'GET'),
  deliverJob:    (id, d)    => req(BACKEND, `/v1/work/jobs/${id}/deliver`,        'POST', d),
  respondDelivery:(jid, did, d) => req(BACKEND, `/v1/work/jobs/${jid}/deliveries/${did}/respond`, 'POST', d),
  addMilestone:  (id, d)    => req(BACKEND, `/v1/work/jobs/${id}/milestones`,     'POST', d),
  sendMessage:   (id, d)    => req(BACKEND, `/v1/work/jobs/${id}/messages`,       'POST', d),
  submitReview:  (id, d)    => req(BACKEND, `/v1/work/jobs/${id}/review`,         'POST', d),
};


export const shop = {
  // Shop management
  myShop:        ()         => req(BACKEND, '/v1/shop/me',                       'GET'),
  saveShop:      d          => req(BACKEND, '/v1/shop/me',                       'POST', d),
  getShop:       id         => req(BACKEND, `/v1/shop/shops/${id}`,              'GET'),
  // Products
  browse:        (filter)   => req(BACKEND, `/v1/shop/products${filter?('?'+new URLSearchParams(filter).toString()):''}`, 'GET'),
  myProducts:    ()         => req(BACKEND, '/v1/shop/products/my',              'GET'),
  createProduct: d          => req(BACKEND, '/v1/shop/products',                 'POST', d),
  getProduct:    id         => req(BACKEND, `/v1/shop/products/${id}`,           'GET'),
  updateProduct: (id, d)    => req(BACKEND, `/v1/shop/products/${id}`,           'PUT', d),
  deleteProduct: id         => req(BACKEND, `/v1/shop/products/${id}`,           'DELETE'),
  // Cart
  getCart:       ()         => req(BACKEND, '/v1/shop/cart',                     'GET'),
  addToCart:     d          => req(BACKEND, '/v1/shop/cart',                     'POST', d),
  updateCartItem:(id, d)    => req(BACKEND, `/v1/shop/cart/items/${id}`,         'PUT', d),
  removeCartItem:id         => req(BACKEND, `/v1/shop/cart/items/${id}`,         'DELETE'),
  clearCart:     ()         => req(BACKEND, '/v1/shop/cart',                     'DELETE'),
  // Checkout & orders
  checkout:      d          => req(BACKEND, '/v1/shop/checkout',                 'POST', d),
  myOrders:      ()         => req(BACKEND, '/v1/shop/orders',                   'GET'),
  mySales:       ()         => req(BACKEND, '/v1/shop/sales',                    'GET'),
  getOrder:      id         => req(BACKEND, `/v1/shop/orders/${id}`,             'GET'),
  shipItem:      (id, d)    => req(BACKEND, `/v1/shop/items/${id}/ship`,         'POST', d),
  confirmReceived:id        => req(BACKEND, `/v1/shop/items/${id}/received`,     'POST'),
  // Reviews
  createReview:  d          => req(BACKEND, '/v1/shop/reviews',                  'POST', d),
  productReviews:id         => req(BACKEND, `/v1/shop/products/${id}/reviews`,   'GET'),
  // Wishlist
  myWishlist:    ()         => req(BACKEND, '/v1/shop/wishlist',                 'GET'),
  addWishlist:   d          => req(BACKEND, '/v1/shop/wishlist',                 'POST', d),
  removeWishlist:id         => req(BACKEND, `/v1/shop/wishlist/${id}`,           'DELETE'),
};

export const concepts = {
  // Public/SSI
  list:          (filter)  => req(BACKEND, `/v1/concepts${filter?('?'+new URLSearchParams(filter).toString()):''}`, 'GET'),
  get:           id        => req(BACKEND, `/v1/concepts/${id}`,              'GET'),
  apply:         (id, d)   => req(BACKEND, `/v1/concepts/${id}/apply`,        'POST', d),
  myApplications:()        => req(BACKEND, '/v1/concepts/my-applications',    'GET'),
  myAssignments: ()        => req(BACKEND, '/v1/concepts/my-assignments',     'GET'),
  categories:    ()        => req(BACKEND, '/v1/categories',                  'GET', null, false),
  // Admin
  adminCreate:   d         => req(BACKEND, '/v1/admin/concepts',              'POST', d),
  adminUpdate:   (id, d)   => req(BACKEND, `/v1/admin/concepts/${id}`,        'PUT',  d),
  adminDelete:   id        => req(BACKEND, `/v1/admin/concepts/${id}`,        'DELETE'),
  adminApps:     ()        => req(BACKEND, '/v1/admin/applications',          'GET'),
  adminReview:   (id, d)   => req(BACKEND, `/v1/admin/applications/${id}/review`, 'POST', d),
};

export const commission = {
  ranks:         ()          => req(BACKEND, '/v1/commission/ranks',               'GET'),
  profile:       ()          => req(BACKEND, '/v1/commission/profile',             'GET'),
  register:      (data)      => req(BACKEND, '/v1/commission/register',            'POST', data),
  team:          ()          => req(BACKEND, '/v1/commission/team',                'GET'),
  myBonuses:     ()          => req(BACKEND, '/v1/commission/my-bonuses',          'GET'),
  mySda:         ()          => req(BACKEND, '/v1/commission/my-sda',              'GET'),
  leaderboard:   (period)    => req(BACKEND, `/v1/commission/leaderboard/${period}`, 'GET'),
  // Admin
  adminConfig:   ()          => req(BACKEND, '/v1/admin/commission/config',        'GET'),
  updateConfig:  (key, val)  => req(BACKEND, `/v1/admin/commission/config/${key}`, 'POST', { value: val }),
  reorderRanks:  (period)    => req(BACKEND, `/v1/admin/commission/reorder/${period}`, 'POST'),
  distribute:    (period)    => req(BACKEND, `/v1/admin/commission/distribute/${period}`, 'POST'),
  unlockSda:     (data)      => req(BACKEND, '/v1/admin/commission/unlock-sda',    'POST', data),
};

export const disputes = {
  myList:        ()           => req(BACKEND, '/v1/disputes',             'GET'),
  raise:         (data)       => req(BACKEND, '/v1/disputes',             'POST', data),
  get:           (id)         => req(BACKEND, `/v1/disputes/${id}`,       'GET'),
  sendMessage:   (id, m)      => req(BACKEND, `/v1/disputes/${id}/messages`, 'POST', { message: m }),
  // Admin
  adminList:     (s='open')   => req(BACKEND, `/v1/admin/disputes?status=${s}`, 'GET'),
  adminResolve:  (id, data)   => req(BACKEND, `/v1/admin/disputes/${id}/resolve`, 'POST', data),
};

export const disputesV2 = {
  mine:          ()        => req(BACKEND, '/v1/disputes/v2/mine',                       'GET'),
  get:           id        => req(BACKEND, `/v1/disputes/v2/${id}`,                      'GET'),
  raise:         (type, id, d) => req(BACKEND, `/v1/disputes/v2/raise/${type}/${id}`,   'POST', d),
  sendMessage:   (id, d)   => req(BACKEND, `/v1/disputes/v2/${id}/messages`,             'POST', d),
  adminList:     (filter)  => req(BACKEND, `/v1/admin/disputes/v2${filter?('?'+new URLSearchParams(filter).toString()):''}`, 'GET'),
  adminResolve:  (id, d)   => req(BACKEND, `/v1/admin/disputes/v2/${id}/resolve`,        'POST', d),
};

export const reviews = {
  submit:        (data)        => req(BACKEND, '/v1/reviews',                 'POST', data),
  getForOrder:   (orderId)     => req(BACKEND, `/v1/reviews/order/${orderId}`, 'GET'),
  forProvider:   (providerId)  => req(BACKEND, `/v1/reviews/provider/${providerId}`, 'GET'),
};

export const chat = {
  listThreads:   ()            => req(BACKEND, '/v1/chat/threads',                        'GET'),
  openForOrder:  (orderId)     => req(BACKEND, `/v1/chat/threads/order/${orderId}`,       'GET'),
  getMessages:   (threadId)    => req(BACKEND, `/v1/chat/threads/${threadId}/messages`,   'GET'),
  sendMessage:   (threadId, m) => req(BACKEND, `/v1/chat/threads/${threadId}/messages`,   'POST', { message: m }),
};

export const notifications = {
  list:          ()      => req(BACKEND, '/v1/notifications',               'GET'),
  unreadCount:   ()      => req(BACKEND, '/v1/notifications/unread-count',  'GET'),
  markRead:      (id)    => req(BACKEND, `/v1/notifications/${id}/read`,    'POST'),
  markAllRead:   ()      => req(BACKEND, '/v1/notifications/mark-all-read', 'POST'),
  remove:        (id)    => req(BACKEND, `/v1/notifications/${id}`,         'DELETE'),
};

export const admin = {
  dashboard:     ()       => req(BACKEND, '/v1/admin/dashboard',      'GET'),
  users:         (page=1) => req(BACKEND, `/v1/admin/users?page=${page}&limit=20`, 'GET'),
  suspend:       (id)     => req(BACKEND, `/v1/admin/users/${id}/suspend`,  'POST'),
  activate:      (id)     => req(BACKEND, `/v1/admin/users/${id}/activate`, 'POST'),
  audit:         ()       => req(BACKEND, '/v1/admin/audit',          'GET'),
  kycQueue:      ()       => req(BACKEND, '/v1/admin/kyc/queue',      'GET'),
  kycReview:     (data)   => req(BACKEND, '/v1/admin/kyc/review',     'POST', data),
  grantWallet:   (data)   => req(BACKEND, '/v1/admin/wallet/grant',   'POST', data),
  // Transactions
  transactions:  (filters={}) => {
    const q = new URLSearchParams(Object.entries(filters).filter(([,v])=>v)).toString();
    return req(BACKEND, '/v1/admin/transactions' + (q?`?${q}`:''), 'GET');
  },
  serviceStats:  ()       => req(BACKEND, '/v1/admin/service-stats',  'GET'),
  revenue:       ()       => req(BACKEND, '/v1/admin/revenue',        'GET'),
  // Moderation
  reports:       (status='pending') => req(BACKEND, `/v1/admin/moderation/reports?status=${status}`, 'GET'),
  reviewReport:  (id, data) => req(BACKEND, `/v1/admin/moderation/reports/${id}`, 'POST', data),
  blockedWords:  ()       => req(BACKEND, '/v1/admin/moderation/blocked-words',   'GET'),
  addBlockedWord:(data)   => req(BACKEND, '/v1/admin/moderation/blocked-words',   'POST', data),
  removeBlockedWord:(w)   => req(BACKEND, `/v1/admin/moderation/blocked-words/${w}`, 'DELETE'),
  // Categories
  categories:    ()       => req(BACKEND, '/v1/admin/categories',     'GET'),
  createCategory:(data)   => req(BACKEND, '/v1/admin/categories',     'POST', data),
  updateCategory:(id, d)  => req(BACKEND, `/v1/admin/categories/${id}`, 'PUT', d),
  deleteCategory:(id)     => req(BACKEND, `/v1/admin/categories/${id}`, 'DELETE'),
};

export const upload = async (file) => {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BACKEND}/v1/uploads`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Upload failed');
  return data;
};

export const rentals = {
  // Items (listings)
  browse:        (filter)   => req(BACKEND, `/v1/rentals/items${filter?('?'+new URLSearchParams(filter).toString()):''}`, 'GET'),
  getItem:       id         => req(BACKEND, `/v1/rentals/items/${id}`,                 'GET'),
  myItems:       ()         => req(BACKEND, '/v1/rentals/items/my',                    'GET'),
  createItem:    d          => req(BACKEND, '/v1/rentals/items',                       'POST', d),
  updateItem:    (id, d)    => req(BACKEND, `/v1/rentals/items/${id}`,                 'PUT', d),
  myClients:     ()         => req(BACKEND, '/v1/rentals/clients',                     'GET'),
  checkAvailability: (id, d) => req(BACKEND, `/v1/rentals/items/${id}/availability${d?('?'+new URLSearchParams(d).toString()):''}`, 'GET'),
  // Bookings
  myBookings:    ()         => req(BACKEND, '/v1/rentals/bookings',                    'GET'),
  receivedBookings: ()      => req(BACKEND, '/v1/rentals/bookings/received',           'GET'),
  createBooking: d          => req(BACKEND, '/v1/rentals/bookings',                    'POST', d),
  getBooking:    id         => req(BACKEND, `/v1/rentals/bookings/${id}`,              'GET'),
  approveBooking:id         => req(BACKEND, `/v1/rentals/bookings/${id}/approve`,      'POST'),
  rejectBooking: id         => req(BACKEND, `/v1/rentals/bookings/${id}/reject`,       'POST'),
  handover:      (id, d)    => req(BACKEND, `/v1/rentals/bookings/${id}/handover`,     'POST', d),
  returnBooking: (id, d)    => req(BACKEND, `/v1/rentals/bookings/${id}/return`,       'POST', d || {}),
  completeBooking: id       => req(BACKEND, `/v1/rentals/bookings/${id}/complete`,     'POST'),
};

export const talent = {
  // Employer profile (recruiter side)
  myEmployerProfile: ()      => req(BACKEND, '/v1/talent/employer/profile/me',         'GET'),
  saveEmployerProfile: d     => req(BACKEND, '/v1/talent/employer/profile',            'POST', d),
  // Seeker profile (applicant side)
  mySeekerProfile:   ()      => req(BACKEND, '/v1/talent/seeker/profile/me',           'GET'),
  saveSeekerProfile: d       => req(BACKEND, '/v1/talent/seeker/profile',              'POST', d),
  // Jobs
  browseJobs:        (filter) => req(BACKEND, `/v1/talent/jobs${filter?('?'+new URLSearchParams(filter).toString()):''}`, 'GET'),
  browseSeekers:     (filter) => req(BACKEND, `/v1/talent/seekers${filter?('?'+new URLSearchParams(filter).toString()):''}`, 'GET'),
  myJobs:            ()       => req(BACKEND, '/v1/talent/jobs/mine',                  'GET'),
  getJob:            id       => req(BACKEND, `/v1/talent/jobs/${id}`,                 'GET'),
  createJob:         d        => req(BACKEND, '/v1/talent/jobs',                       'POST', d),
  closeJob:          id       => req(BACKEND, `/v1/talent/jobs/${id}/close`,           'POST'),
  updateJob:         (id, d)  => req(BACKEND, `/v1/talent/jobs/${id}`,                 'PUT', d),
  autoShortlist:     id       => req(BACKEND, `/v1/talent/jobs/${id}/shortlist`,       'GET'),
  // Applications
  applyToJob:        (id, d)  => req(BACKEND, `/v1/talent/jobs/${id}/apply`,           'POST', d),
  jobApplications:   id       => req(BACKEND, `/v1/talent/jobs/${id}/applications`,    'GET'),
  myApplications:    ()       => req(BACKEND, '/v1/talent/applications/mine',          'GET'),
  shortlistApp:      id       => req(BACKEND, `/v1/talent/applications/${id}/shortlist`, 'POST'),
  rejectApp:         id       => req(BACKEND, `/v1/talent/applications/${id}/reject`,  'POST'),
  hireApplicant:     (id, d)  => req(BACKEND, `/v1/talent/applications/${id}/hire`,    'POST', d),
};

export const dating = {
  saveProfile: (data) => req(BACKEND, '/v1/dating/profile', 'POST', data),
  myProfile: () => req(BACKEND, '/v1/dating/profile', 'GET'),
  getProfile: (id) => req(BACKEND, `/v1/dating/profile/${id}`, 'GET'),
  browse: (city) => req(BACKEND, `/v1/dating/browse?city=${encodeURIComponent(city||'')}`, 'GET'),
  myRequests: (dir) => req(BACKEND, `/v1/dating/requests?dir=${dir||'incoming'}`, 'GET'),
  proposeDay: (data) => req(BACKEND, '/v1/dating/requests', 'POST', data),
  respond: (id, action) => req(BACKEND, `/v1/dating/requests/${id}/respond`, 'POST', {action}),
  confirmMet: (id) => req(BACKEND, `/v1/dating/requests/${id}/met`, 'POST', {}),
  setContinue: (id, want) => req(BACKEND, `/v1/dating/requests/${id}/continue`, 'POST', {continue: want}),
};

export const meetup = {
  saveProfile: (data) => req(BACKEND, '/v1/meetup/profile', 'POST', data),
  myProfile: () => req(BACKEND, '/v1/meetup/profile', 'GET'),
  getProfile: (id) => req(BACKEND, `/v1/meetup/profile/${id}`, 'GET'),
  browse: (city) => req(BACKEND, `/v1/meetup/browse?city=${encodeURIComponent(city||'')}`, 'GET'),
  myRequests: (dir) => req(BACKEND, `/v1/meetup/requests?dir=${dir||'incoming'}`, 'GET'),
  proposeMeetup: (data) => req(BACKEND, '/v1/meetup/requests', 'POST', data),
  respond: (id, action) => req(BACKEND, `/v1/meetup/requests/${id}/respond`, 'POST', {action}),
  confirmMet: (id) => req(BACKEND, `/v1/meetup/requests/${id}/met`, 'POST', {}),
  setContinue: (id, want) => req(BACKEND, `/v1/meetup/requests/${id}/continue`, 'POST', {continue: want}),
  review: (data) => req(BACKEND, '/v1/meetup/review', 'POST', data),
  block: (id) => req(BACKEND, '/v1/meetup/block', 'POST', {user_id: id}),
  report: (data) => req(BACKEND, '/v1/meetup/report', 'POST', data),
};

export const catTree = {
  tree: (parentId) => req(BACKEND, parentId ? `/v1/cat/tree?parent_id=${encodeURIComponent(parentId)}` : '/v1/cat/tree', 'GET'),
  path: (id) => req(BACKEND, `/v1/cat/path/${encodeURIComponent(id)}`, 'GET'),
  superTiles: () => req(BACKEND, '/v1/cat/super-tiles', 'GET'),
};

export const quickOrder = {
  getLast: (category) => req(BACKEND, `/v1/quick/last/${encodeURIComponent(category)}`, 'GET'),
};

export const earns = {
  config: () => req(BACKEND, '/v1/earns/config', 'GET'),
  summary: () => req(BACKEND, '/v1/earns/summary', 'GET'),
  award: (data) => req(BACKEND, '/v1/earns/award', 'POST', data),
  reviewDate: (data) => req(BACKEND, '/v1/dating/review', 'POST', data),
  block: (blocked_id, reason) => req(BACKEND, '/v1/dating/block', 'POST', {blocked_id, reason}),
  report: (data) => req(BACKEND, '/v1/dating/report', 'POST', data),
};
